import { useState } from "react";
import { addConduit, addSubcatchment } from "../model/inp/editNetwork.ts";
import { drawnConduitLength } from "../model/inp/conduitLength.ts";
import { getCaseInsensitive } from "../model/inp/idLookup.ts";
import {
  mergeSelection,
  sameElementRef,
  selectByKind,
  selectWithinPolygon,
} from "../model/inp/selection.ts";
import { THEMATIC_ATTRIBUTES } from "../model/inp/thematicAttributes.ts";
import type { ElementCategory, ElementRef } from "../model/inp/properties.ts";
import type { Point, SwmmProject } from "../model/inp/types.ts";
import type { StoredProject } from "../model/storage.ts";
import type { ElementDefaults } from "../model/elementDefaults.ts";
import { ADD_LINK, ADD_POINT, addPointToolRef, type EditMode } from "./editModeTools.ts";
import { resolveBackgroundPick, resolveElementPick } from "./mapPickDispatch.ts";
import type { useLongitudinalProfile } from "./useLongitudinalProfile.ts";
import type { useNetworkTrace } from "./useNetworkTrace.ts";
import type { useMapQuery } from "./useMapQuery.ts";

type ThematicAttributeDef = (typeof THEMATIC_ATTRIBUTES)[number];

interface UseMapInteractionOptions {
  project: SwmmProject | null;
  loaded: StoredProject | null;
  elementDefaults: ElementDefaults;
  selected: ElementRef | null;
  setSelected: (ref: ElementRef | null) => void;
  multiSelected: ElementRef[];
  setMultiSelected: (refs: ElementRef[]) => void;
  multiSelectMode: boolean;
  bumpRecenterToken: () => void;
  mutateRawUndoable: (
    fn: (raw: ReadonlyMap<string, string[][]>) => ReadonlyMap<string, string[][]>
  ) => void;
  longitudinalProfile: ReturnType<typeof useLongitudinalProfile>;
  networkTrace: ReturnType<typeof useNetworkTrace>;
  mapQuery: ReturnType<typeof useMapQuery>;
  mapQueryMatches: Set<string> | null;
  mapQueryAttributeDef: ThematicAttributeDef | null;
}

/**
 * The edit-mode toolbar's own `mode` plus every click/area-select dispatch
 * that reads it — a plain click/shift-click/area-select in "select" mode, an
 * ADD_LINK tool's two-click node-to-node flow, an ADD_POINT tool's one-click
 * placement, and a subcatchment's multi-click outline draft. `conduitStart`/
 * `subcatchmentDraft` are this concern's own in-progress-tool state, cleared
 * on every mode change alongside `mode` itself by `changeMode`.
 */
export function useMapInteraction(opts: UseMapInteractionOptions) {
  const [mode, setMode] = useState<EditMode>("select");
  // The first node clicked while adding a conduit — cleared on every mode
  // change so switching away mid-pick can't leave a stale state behind.
  const [conduitStart, setConduitStart] = useState<string | null>(null);
  // The points placed so far while drawing a new subcatchment's outline —
  // same "cleared on every mode change" lifetime as conduitStart above.
  const [subcatchmentDraft, setSubcatchmentDraft] = useState<Point[]>([]);

  function changeMode(next: EditMode) {
    setMode(next);
    setConduitStart(null);
    setSubcatchmentDraft([]);
  }

  /**
   * Routes a map/browser click through whatever tool is active. In "select"
   * mode a plain click replaces both `selected` and `multiSelected`; a
   * SHIFT-click (map only) toggles `ref` in/out of `multiSelected` instead,
   * and always makes it the new `selected` too. In any ADD_LINK mode, a node
   * click is the tool's own input instead: the first click picks the start
   * node, the second (a DIFFERENT node) creates the link and drops back to
   * "select" — clicking the SAME node twice is a no-op, not a self-loop.
   * `shiftKey` is ignored there.
   *
   * Checked BEFORE any of that: `resolveElementPick` — `LongitudinalProfileDialog`'s
   * own "pick on the map" mode, regardless of `mode` — a non-node click
   * while picking is simply ignored (a profile only ever runs node-to-node).
   */
  function handleElementClick(ref: ElementRef, shiftKey = false) {
    if (
      resolveElementPick(
        ref,
        opts.project,
        opts.longitudinalProfile,
        opts.networkTrace,
        opts.setSelected
      )
    ) {
      return;
    }
    const linkTool = ADD_LINK[mode];
    if (linkTool && ref.category === "node") {
      if (!conduitStart) {
        setConduitStart(ref.name);
        opts.setSelected(ref);
        return;
      }
      if (conduitStart !== ref.name) {
        // Auto-length: a freshly drawn conduit gets its Length from the
        // straight-line distance between the two just-clicked nodes,
        // instead of the "400" placeholder `addConduit` falls back to.
        // `drawnConduitLength` (not the raw coordinate-plane distance) also
        // handles a geographic CRS's degree-based coordinates correctly.
        const from = opts.project && getCaseInsensitive(opts.project.coordinates, conduitStart);
        const to = opts.project && getCaseInsensitive(opts.project.coordinates, ref.name);
        const length =
          from && to && opts.project
            ? drawnConduitLength(from, to, opts.loaded?.crs ?? null, opts.project.flowUnits)
            : undefined;
        opts.mutateRawUndoable((raw) =>
          linkTool.kind === "conduit"
            ? addConduit(raw, conduitStart, ref.name, length, opts.elementDefaults).raw
            : linkTool.fn(raw, conduitStart, ref.name, opts.elementDefaults).raw
        );
        setMode("select");
        setConduitStart(null);
      }
      return;
    }
    if (shiftKey || opts.multiSelectMode) {
      // The FIRST shift-click of a new sequence starts from whatever
      // plain-click `selected` already picked, so that element isn't
      // silently dropped out of the group — found live: shift-clicking a
      // second node right after a plain click on a first one left the first
      // one unhighlighted and out of `elementsToCopy`.
      const base =
        opts.multiSelected.length > 0 ? opts.multiSelected : opts.selected ? [opts.selected] : [];
      const already = base.some((r) => sameElementRef(r, ref));
      opts.setMultiSelected(already ? base.filter((r) => !sameElementRef(r, ref)) : [...base, ref]);
      opts.setSelected(ref);
      return;
    }
    opts.setMultiSelected([]);
    opts.setSelected(ref);
  }

  /** ElementBrowser/ElementSearch's own `onSelect` — same selection logic as a map click, plus the recenter request a map click deliberately skips. */
  function selectFromList(ref: ElementRef, shiftKey = false) {
    handleElementClick(ref, shiftKey);
    opts.bumpRecenterToken();
  }

  function handleBackgroundClick(point: Point) {
    if (resolveBackgroundPick(opts.longitudinalProfile, opts.networkTrace)) return;
    if (mode === "select") {
      // While "multiple" is toggled on, clicking empty space shouldn't
      // discard the group being built up one click at a time.
      if (!opts.multiSelectMode) {
        opts.setSelected(null);
        opts.setMultiSelected([]);
      }
      return;
    }
    // Rectangle/lasso select has its own dedicated path (onAreaSelect, fired
    // once a drag ends) — a plain click here reads as "selected nothing".
    if (mode === "select-rect" || mode === "select-lasso") return;
    if (mode === "add-subcatchment") {
      setSubcatchmentDraft((points) => [...points, point]);
      return;
    }
    const pointTool = ADD_POINT[mode];
    if (!pointTool || !opts.loaded) return;
    // Computed eagerly, not read back out of mutateRaw's updater callback —
    // that callback isn't guaranteed to run before setSelected below reads it.
    const result = pointTool.fn(opts.loaded.rawSections, point, opts.elementDefaults);
    opts.mutateRawUndoable(() => result.raw);
    opts.setSelected(addPointToolRef(pointTool, result.name));
    setMode("select");
  }

  function finishSubcatchmentDraft() {
    if (subcatchmentDraft.length < 3 || !opts.loaded) return;
    const result = addSubcatchment(
      opts.loaded.rawSections,
      subcatchmentDraft,
      opts.elementDefaults
    );
    opts.mutateRawUndoable(() => result.raw);
    opts.setSelected({ category: "subcatchment", kind: "subcatchment", name: result.name });
    setMode("select");
    setSubcatchmentDraft([]);
  }

  /** `onAreaSelect` — a rectangle or lasso drag just ended, `polygon` is the dragged shape in real project coordinates. Same "one shot, then back to plain select" convention as every add-* tool. */
  function handleAreaSelect(polygon: Point[]) {
    if (!opts.project) return;
    const matches = selectWithinPolygon(opts.project, polygon);
    opts.setMultiSelected(mergeSelection(opts.multiSelected, matches, opts.multiSelectMode));
    opts.setSelected(matches[0] ?? null);
    changeMode("select");
  }

  /** SelectionToolsMenu's own "Por tipo" rows — every element of one category+kind, merged into the current selection the same way `handleAreaSelect` does. Doesn't arm any mode — an immediate action. */
  function handleSelectByType(category: ElementCategory, kind: string) {
    if (!opts.project) return;
    const matches = selectByKind(opts.project, category, kind);
    opts.setMultiSelected(mergeSelection(opts.multiSelected, matches, opts.multiSelectMode));
    opts.setSelected(matches[0] ?? null);
  }

  /** Converts the already-computed `mapQueryMatches` (bare names) into real `ElementRef[]` — the query only knows the CATEGORY, never the more specific kind, so this looks each match back up against `project` — merges into the current selection, and closes the bar. */
  function handleSelectMapQueryMatches() {
    if (!opts.project || !opts.mapQueryMatches || !opts.mapQueryAttributeDef) return;
    const category = opts.mapQueryAttributeDef.category;
    const matches: ElementRef[] =
      category === "node"
        ? opts.project.nodes
            .filter((n) => opts.mapQueryMatches!.has(n.name))
            .map((n) => ({ category, kind: n.kind, name: n.name }))
        : category === "link"
          ? opts.project.links
              .filter((l) => opts.mapQueryMatches!.has(l.name))
              .map((l) => ({ category, kind: l.kind, name: l.name }))
          : opts.project.subcatchments
              .filter((s) => opts.mapQueryMatches!.has(s.name))
              .map((s) => ({ category, kind: "subcatchment" as const, name: s.name }));
    opts.setMultiSelected(mergeSelection(opts.multiSelected, matches, opts.multiSelectMode));
    opts.setSelected(matches[0] ?? null);
    opts.mapQuery.close();
  }

  return {
    mode,
    conduitStart,
    subcatchmentDraft,
    changeMode,
    handleElementClick,
    selectFromList,
    handleBackgroundClick,
    finishSubcatchmentDraft,
    handleAreaSelect,
    handleSelectByType,
    handleSelectMapQueryMatches,
  };
}
